import { Customer } from './customer.model';
import { LateFeeType } from './late-fee-policy.model';
import { ProductAttribute } from './product.model';

export interface OrderList {
  id: number;
  number: string;
  status: OrderStatus;
  placedAt: Date;
  fullName: string;
  productName: string;
  fullPrice: string;
}

export interface Order {
  id: string;
  number: string;
  status: OrderStatus;
  placedAt: Date;
  expectedDeliveryDate: Date;
  note: string | null;
  histories: OrderStatusHistory[];
  eligibilityAssessment: EligibilityAssessment | null;
  documents: OrderDocument[];
  productIdentifiers: ProductIdentifier[];
  shippingAddress: ShippingAddress;
  product: OrderProduct;
  customer: Customer;
  shipments: Shipment[];
  offer: OrderOffer;
  paymentPlan: OrderPaymentPlan;
  contract?: OrderContract | null;
  paymentCards?: OrderPaymentCard[];
  cancellation?: OrderCancellation | null;
  approvalPrerequisites?: OrderApprovalPrerequisites | null;
}

export interface OrderApprovalPrerequisites {
  hasTaxRate: boolean;
  hasContractTemplate: boolean;
}

export interface OrderCancellation {
  id: string;
  reasonId: string;
  reasonName?: string | null;
  reasonDescription: string | null;
  requestedAt: Date;
  additionalDetails: string | null;
  cancelledByEmployeeName: string | null;
}

export interface OrderStatusHistory {
  id: string;
  from: OrderStatus;
  to: OrderStatus;
  transitionedAt: Date;
}

export interface EligibilityAssessment {
  id: string;
  status: EligibilityAssessmentStatus;
  note: string | null;
  startedAt: string;
  completedAt: string | null;
  rulesEvaluations: EligibilityRuleEvaluation[];
}

export interface EligibilityRuleEvaluation {
  id: string;
  assessmentRuleId: string;
  assessmentRuleName: string;
  assessmentRuleCondition: string;
  status: EligibilityRuleAvaluationStatus;
  failureReason: string | null;
  note: string | null;
}

export interface OrderDocument {
  id: string;
  type: OrderDocumentType;
  uploadedAt: Date;
  fileUrl: string;
}

export interface ProductIdentifier {
  id: string;
  productIdentifierTypeId: string;
  productIdentifierTypeName: string;
  productIdentifierTypeDescription: string;
  value: string;
  note: string | null;
}

export interface ShippingAddress {
  street: string;
  number?: string | null;
  complement: string | null;
  neighborhood?: string | null;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export interface OrderProduct {
  id: string;
  modelVersionId: string;
  modelVersionName: string;
  modelId: string;
  modelName: string;
  name: string;
  sku: string;
  mainImageUrl?: string | null;
  attributes: ProductAttribute[];
}

export interface Shipment {
  id: string;
  carrierId: string;
  carrierName: string;
  carrierWebsiteUrl?: string | null;
  carrierTrackingUrl?: string | null;
  trackingCode: string;
  status: ShipmentStatus;
  shippedAt: string | null;
  estimatedDeliveryDate: string | null;
  deliveredAt: string | null;
  note: string | null;
  histories: ShipmentStatusHistory[];
}

export interface OrderOffer {
  id: string;
  fullPrice: number;
  downPayment: number;
  installmentCount: number;
  installmentAmount: number;
  paymentFrequencyName: string;
  paymentFrequencyIntervalInDays: number;
}

export interface OrderPaymentPlan {
  id: string;
  installmentCount: number;
  installmentAmount: number;
  downPayment: number;
  fullPrice: number;
  cashPrice?: number | null;
  intervalInDays: number;
  interestRate: number;
  lateFeePolicyId?: string | null;
  lateFeeType?: LateFeeType;
  lateFeeValue?: number | null;
  lateFeeGraceDays?: number | null;
  lateFeeIsTaxable?: boolean | null;
  installments: OrderPaymentInstallment[];
  paymentFrequencyName: string;
}

export interface OrderPaymentInstallment {
  id: string;
  status: InstallmentStatus;
  number: number;
  amount: number;
  taxAmount?: number | null;
  taxRate?: number | null;
  lateFeeAmount?: number | null;
  lateFeeTaxAmount?: number | null;
  lateFeeAppliedAt?: Date | null;
  totalAmount: number;
  dueDate: Date;
  payments: OrderPaymentInstallmentDetails[];
  invoice?: InstallmentInvoice | null;
}

export interface OrderContract {
  id: string;
  status: ContractStatus;
  contractUrl: string;
  sentForSignatureAt: Date;
  signedAt: Date;
  signatureProvider: string;
  signatureProviderId: string;
}

export interface ContractStatusTag {
  status: string;
  text: string;
}

export interface CreateContractResponse {
  id: string;
  contractUrl: string;
}

export interface OrderPaymentCard {
  id: string;
  cardId: string;
  isDefault: true;
  type: string;
  displayNumber: string;
  brand: string;
  expirationMonth: number;
  expirationYear: number;
  holderName: string;
}

export interface ShipmentStatusHistory {
  id: string;
  from: ShipmentStatus;
  to: ShipmentStatus;
  transitionedAt: Date;
}

export interface OrderPaymentInstallmentDetails {
  amount: number;
  confirmedAt: Date;
  method: PaymentMethod;
  providerPaymentId: string;
  status: PaymentStatus;
}

export interface InstallmentInvoice {
  id: string;
  status: InvoiceStatus;
  invoiceUrl: string;
}

export enum OrderStatus {
  DocumentsPending = 1,
  DocumentsSubmitted = 2,
  UnderReview = 3,
  Rejected = 4,
  ContractPending = 5,
  PaymentPending = 6,
  Confirmed = 7,
  Preparing = 8,
  Shipped = 9,
  Delivered = 10,
  Cancelled = 11,
  Reserved = 12,
}

export enum OrderDocumentType {
  ProofOfAddress = 1,
  BankStatement = 2,
  FaceCapture = 3,
  Passport = 4,
}

export enum EligibilityAssessmentStatus {
  Pending = 1,
  InProgress = 2,
  Approved = 3,
  Rejected = 4,
}

export enum EligibilityRuleAvaluationStatus {
  Pending = 1,
  Approved = 2,
  Rejected = 3,
  Skipped = 4,
}

export enum ShipmentStatus {
  AwaitingPickup = 1,
  PickedUp = 2,
  InTransit = 3,
  OutForDelivery = 4,
  Delivered = 5,
  DeliveryException = 6,
  ReturnedToSender = 7,
}

export enum InstallmentStatus {
  Pending = 1,
  Paid = 2,
  PartiallyPaid = 3,
  Overdue = 4,
  Cancelled = 5,
  Suspended = 6,
  Refunded = 7,
  PendingRefund = 8,
}

export enum ContractStatus {
  Generated = 1,
  SentForSignature = 2,
  Signed = 3,
  Cancelled = 4,
  Declined = 5,
}

export enum PaymentMethod {
  CreditCard = 1,
  DebitCard = 2,
  BankTransfer = 3,
  BankSlip = 4,
}

export enum PaymentStatus {
  Pending = 1,
  Processing = 2,
  Confirmed = 3,
  Failed = 4,
  Refunded = 5,
  Cancelled = 6,
}

export enum InvoiceStatus {
  Pending = 1,
  Created = 2,
  Sent = 3,
}
